import { useEffect, useState, useMemo } from 'react';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card.jsx';
import { ChevronLeft, ChevronRight, Building2, MapPin, Palmtree, Users, Calendar, CalendarDays } from 'lucide-react';
import { cn } from '@/lib/utils';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ManagerTeamView() {
  const toast = useToast();
  const { user } = useCurrentUser();
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Compute date range based on view mode
  const { dates, label, startDate, endDate } = useMemo(() => {
    if (viewMode === 'week') {
      const d = new Date();
      d.setDate(d.getDate() + offset * 7);
      d.setDate(d.getDate() - d.getDay()); // Go to Sunday
      const arr = [];
      for (let i = 0; i < 7; i++) {
        const dd = new Date(d);
        dd.setDate(dd.getDate() + i);
        arr.push(dd.toISOString().substring(0, 10));
      }
      const s = new Date(arr[0] + 'T12:00:00');
      const e = new Date(arr[6] + 'T12:00:00');
      return {
        dates: arr,
        label: `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
        startDate: arr[0],
        endDate: arr[6],
      };
    } else {
      const d = new Date();
      d.setMonth(d.getMonth() + offset);
      const year = d.getFullYear();
      const month = d.getMonth();
      const dim = new Date(year, month + 1, 0).getDate();
      const ms = String(month + 1).padStart(2, '0');
      const arr = [];
      for (let i = 1; i <= dim; i++) {
        arr.push(`${year}-${ms}-${String(i).padStart(2, '0')}`);
      }
      return {
        dates: arr,
        label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        startDate: arr[0],
        endDate: arr[arr.length - 1],
      };
    }
  }, [viewMode, offset]);

  useEffect(() => {
    setLoading(true);
    employeeApi.getTeamCalendar(startDate, endDate)
      .then((res) => setData(res))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const holidaySet = useMemo(() => new Set((data?.holidays || []).map((h) => h.date)), [data?.holidays]);

  // Build people rows
  const people = useMemo(() => {
    if (!data) return [];
    const map = {};

    for (const l of data.leave || []) {
      if (!map[l.userId]) map[l.userId] = { name: l.userName, dept: l.department, days: {} };
      const d = new Date(l.startDate);
      const end = new Date(l.endDate);
      while (d <= end) {
        const ds = d.toISOString().substring(0, 10);
        if (ds >= startDate && ds <= endDate) {
          map[l.userId].days[ds] = { type: 'leave', status: l.status, typeName: l.typeName, color: l.typeColor || '#3985FD' };
        }
        d.setDate(d.getDate() + 1);
      }
    }

    for (const s of data.schedule || []) {
      if (!map[s.userId]) map[s.userId] = { name: s.userName, dept: s.department, days: {} };
      if (!map[s.userId].days[s.date]) {
        map[s.userId].days[s.date] = { type: s.type, label: s.label, color: s.color };
      }
    }

    return Object.entries(map)
      .map(([id, info]) => ({ id, ...info }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, startDate, endDate]);

  const todayStr = new Date().toISOString().substring(0, 10);
  const resetLabel = viewMode === 'week' ? 'This Week' : 'This Month';

  function renderCell(ds, dayInfo) {
    const isHoliday = holidaySet.has(ds);
    const isToday = ds === todayStr;
    let bg = '';
    let icon = null;
    let cellLabel = '';
    let textColor = 'text-muted-foreground/40';

    if (isHoliday) {
      bg = 'bg-profun-orange/8'; cellLabel = 'H'; textColor = 'text-profun-orange';
    } else if (dayInfo?.type === 'leave') {
      if (dayInfo.status === 'approved') {
        cellLabel = viewMode === 'week' ? (dayInfo.typeName || 'Leave') : 'L';
      } else {
        cellLabel = viewMode === 'week' ? `${dayInfo.typeName || 'Leave'} *` : 'L*';
      }
    } else if (dayInfo?.type) {
      // Any work-location category (Remote, Off Site, or admin-added) —
      // its color comes from the config via the API.
      const c = dayInfo.color || '#64748b';
      icon = viewMode === 'week' ? <MapPin className="h-3 w-3" style={{ color: c }} /> : null;
      cellLabel = viewMode === 'week'
        ? (dayInfo.label || dayInfo.type)
        : (dayInfo.label || dayInfo.type || '?').charAt(0).toUpperCase();
    } else {
      icon = viewMode === 'week' ? <Building2 className="h-3 w-3" /> : null;
      cellLabel = viewMode === 'week' ? 'Office' : '';
    }

    const isWorkLoc = dayInfo?.type && dayInfo.type !== 'leave';
    const cellStyle = dayInfo?.type === 'leave'
      ? {
          color: dayInfo.status === 'pending' ? '#B45309' : dayInfo.color,
          background: dayInfo.status === 'pending' ? 'rgba(180,83,9,0.08)' : (dayInfo.color || '#3985FD') + '12',
        }
      : isWorkLoc
        ? { color: dayInfo.color || '#64748b', background: (dayInfo.color || '#64748b') + '14' }
        : undefined;

    return (
      <td key={ds} className={cn(viewMode === 'week' ? 'text-center py-2 px-1' : 'text-center py-1 px-0.5', isToday && 'bg-primary/[0.03]')}>
        <div
          className={cn(
            'rounded-md font-medium flex flex-col items-center gap-0.5',
            viewMode === 'week' ? 'py-1.5 px-1 text-[10px]' : 'py-0.5 px-0.5 text-[8px]',
            bg, textColor
          )}
          style={cellStyle}
          title={dayInfo?.type === 'leave' ? `${dayInfo.typeName}${dayInfo.status === 'pending' ? ' (pending)' : ''}` : cellLabel}
        >
          {icon}
          <span className={viewMode === 'week' ? 'truncate max-w-[80px]' : ''}>{cellLabel}</span>
        </div>
      </td>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Team Overview</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {viewMode === 'week' ? 'Weekly' : 'Monthly'} view of your team's schedule
          </p>
        </div>
        {/* View mode toggle */}
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            onClick={() => { setViewMode('week'); setOffset(0); }}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors', viewMode === 'week' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/50')}
          >
            <Calendar className="h-3.5 w-3.5" /> Week
          </button>
          <button
            onClick={() => { setViewMode('month'); setOffset(0); }}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-border', viewMode === 'month' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/50')}
          >
            <CalendarDays className="h-3.5 w-3.5" /> Month
          </button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">{label}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setOffset((o) => o - 1)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setOffset(0)} className="rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground">{resetLabel}</button>
            <button onClick={() => setOffset((o) => o + 1)} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              <span className="ml-3">Loading...</span>
            </div>
          ) : people.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">No team schedule data for this period.</div>
          ) : (
            <>
              {/* Legend */}
              <div className="mb-4 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> Office</span>
                {(data?.workLocations || []).filter((w) => w.active).map((w) => (
                  <span key={w.id} className="flex items-center gap-1"><MapPin className="h-3 w-3" style={{ color: w.color }} /> {w.label}</span>
                ))}
                <span className="flex items-center gap-1"><Palmtree className="h-3 w-3 text-profun-blue" /> Leave</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-warning/50" /> Pending</span>
                <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-profun-orange/50" /> Holiday</span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 pr-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground w-[140px] min-w-[140px] sticky left-0 bg-card z-10">Person</th>
                      {dates.map((ds) => {
                        const d = new Date(ds + 'T12:00:00');
                        const isToday = ds === todayStr;
                        return (
                          <th key={ds} className={cn(
                            'text-center py-2 font-semibold uppercase tracking-wider',
                            viewMode === 'week' ? 'px-1 text-[11px] min-w-[90px]' : 'px-0.5 text-[9px] min-w-[32px]',
                            isToday ? 'text-primary' : 'text-muted-foreground'
                          )}>
                            {viewMode === 'week' ? (
                              <>
                                <div>{DAY_NAMES[d.getDay()]}</div>
                                <div className="text-[10px] font-normal">{d.getDate()}</div>
                              </>
                            ) : (
                              <div>{d.getDate()}</div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((person) => (
                      <tr key={person.id} className="border-b border-border/50 last:border-0">
                        <td className="py-2 pr-4 sticky left-0 bg-card z-10">
                          <div className="text-sm font-medium text-foreground truncate">{person.name}</div>
                          {person.dept && <div className="text-[10px] text-muted-foreground">{person.dept}</div>}
                        </td>
                        {dates.map((ds) => renderCell(ds, person.days[ds]))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 pt-4 border-t border-border flex gap-4 text-xs text-muted-foreground">
                <span>{people.length} team member{people.length !== 1 ? 's' : ''}</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
